import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronLeft } from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import BrandMark from '../components/BrandMark';

type AuthProps = {
  initialMode?: 'login' | 'signup';
};

const authBackgroundTerms = [
  { label: 'DOPAMINA', size: 'text-3xl md:text-6xl', top: '10%', left: '12%', speed: 0.12, drift: -10, delay: '0s', duration: '12s' },
  { label: 'FOCO', size: 'text-4xl md:text-7xl', top: '18%', left: '62%', speed: 0.16, drift: 16, delay: '1s', duration: '10s' },
  { label: 'EXECUCAO', size: 'text-2xl md:text-5xl', top: '34%', left: '8%', speed: 0.13, drift: 10, delay: '1.8s', duration: '13s' },
  { label: 'HIPERFOCO', size: 'text-2xl md:text-5xl', top: '44%', left: '58%', speed: 0.15, drift: -14, delay: '0.7s', duration: '11s' },
  { label: 'CONSISTENCIA', size: 'text-xl md:text-4xl', top: '62%', left: '14%', speed: 0.14, drift: 12, delay: '2.2s', duration: '12s' },
  { label: 'AURA', size: 'text-3xl md:text-6xl', top: '76%', left: '64%', speed: 0.12, drift: -12, delay: '1.4s', duration: '10s' },
  { label: 'RECOMECO', size: 'text-lg md:text-4xl', top: '88%', left: '24%', speed: 0.11, drift: 8, delay: '2.8s', duration: '14s' },
];

const Auth = ({ initialMode = 'login' }: AuthProps) => {
  const navigate = useNavigate();
  const authRef = useRef<HTMLDivElement | null>(null);
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [authView, setAuthView] = useState<'auth' | 'forgot-password' | 'reset-password'>('auth');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const checkRecoverySession = async () => {
      const { data } = await supabase.auth.getSession();
      const isRecoveryFlow = typeof window !== 'undefined' && window.location.hash.includes('type=recovery');

      if (data.session && isRecoveryFlow) {
        setAuthView('reset-password');
      }
    };

    void checkRecoverySession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setAuthView('reset-password');
        setError('');
        setSuccessMessage('');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    let frameId = 0;

    const syncScrollVariable = () => {
      frameId = 0;
      authRef.current?.style.setProperty('--landing-scroll-y', `${window.scrollY}px`);
    };

    const handleScroll = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(syncScrollVariable);
    };

    syncScrollVariable();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          setError(error.message);
        } else {
          navigate('/app/home');
        }
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
        });

        if (error) {
          setError(error.message);
        } else {
          setSuccessMessage('Verifique seu email para confirmar a conta.');
        }
      }
    } catch (err) {
      setError('Erro inesperado. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const redirectTo = `${window.location.origin}${window.location.pathname}#/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });

      if (error) {
        setError(error.message);
      } else {
        setSuccessMessage('Enviamos um link de recuperação para o seu email.');
      }
    } catch (err) {
      setError('Erro inesperado ao solicitar recuperação de senha.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      if (password.length < 6) {
        setError('A nova senha precisa ter pelo menos 6 caracteres.');
        return;
      }

      if (password !== confirmPassword) {
        setError('As senhas não coincidem.');
        return;
      }

      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        setError(error.message);
      } else {
        setSuccessMessage('Senha atualizada com sucesso. Agora você já pode entrar.');
        setAuthView('auth');
        setIsLogin(true);
        setPassword('');
        setConfirmPassword('');
      }
    } catch (err) {
      setError('Erro inesperado ao redefinir a senha.');
    } finally {
      setLoading(false);
    }
  };

  const authTitle = authView === 'reset-password'
    ? 'Defina sua nova senha.'
    : authView === 'forgot-password'
      ? 'Recupere seu acesso.'
      : 'Hackeie sua dopamina. Farme Aura.';

  return (
    <div ref={authRef} className="min-h-screen bg-background flex flex-col items-center justify-center p-6 relative overflow-hidden text-white">
      <div className="pointer-events-none fixed inset-0 z-0 bg-[radial-gradient(circle_at_top,rgba(168,85,247,0.18),transparent_30%),radial-gradient(circle_at_78%_16%,rgba(91,33,182,0.16),transparent_24%),radial-gradient(circle_at_bottom,rgba(168,85,247,0.12),transparent_22%)]" />
      <div className="pointer-events-none fixed inset-0 z-0 bg-[linear-gradient(180deg,rgba(7,7,13,0.24),rgba(7,7,13,0.72)_44%,rgba(7,7,13,0.92))]" />
      <div className="landing-word-cloud" aria-hidden="true">
        {authBackgroundTerms.map((term) => (
          <span
            key={term.label}
            className="landing-word-shell"
            style={{
              top: term.top,
              left: term.left,
              ['--landing-speed' as string]: term.speed,
              ['--landing-drift' as string]: term.drift,
            }}
          >
            <span
              className={`landing-word ${term.size}`}
              style={{
                animationDelay: term.delay,
                animationDuration: term.duration,
              }}
            >
              {term.label}
            </span>
          </span>
        ))}
      </div>

      <Link
        to="/"
        className="absolute left-5 top-5 z-20 inline-flex items-center gap-1 rounded-full border border-gray-800 bg-card/80 px-3 py-2 text-xs font-semibold text-gray-400 transition-colors hover:text-white md:left-6 md:top-6"
      >
        <ChevronLeft size={14} />
        Voltar
      </Link>

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <BrandMark
              showTitle={false}
              imageClassName="h-16 w-16 rounded-[22px] border border-neon-purple/20 bg-neon-purple/10 p-2 shadow-[0_0_28px_rgba(168,85,247,0.16)]"
            />
          </div>
          <h1 className="text-4xl font-display font-bold tracking-tight text-white mb-2">
            DOPAMIND
          </h1>
          <p className="text-gray-400">{authTitle}</p>
        </div>

        <div className="bg-card border border-gray-800 rounded-2xl p-6 md:p-8 shadow-xl">
          {successMessage && (
            <div className="mb-4 p-3 bg-neon-green/10 border border-neon-green/30 rounded-lg text-neon-green text-sm">
              {successMessage}
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-900/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {authView === 'forgot-password' ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="player@dopamind.app"
                  className="w-full bg-background border border-gray-800 rounded-lg p-4 text-white placeholder-gray-600 focus:outline-none focus:border-neon-purple focus:ring-1 focus:ring-neon-purple transition-all"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-neon-purple hover:bg-purple-600 text-white font-bold py-4 rounded-lg transition-all transform active:scale-95 flex items-center justify-center space-x-2 mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <span>Enviando...</span> : <span>ENVIAR LINK DE RECUPERAÇÃO</span>}
              </button>
            </form>
          ) : authView === 'reset-password' ? (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-2">Nova senha</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-background border border-gray-800 rounded-lg p-4 text-white placeholder-gray-600 focus:outline-none focus:border-neon-purple focus:ring-1 focus:ring-neon-purple transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-2">Confirmar nova senha</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-background border border-gray-800 rounded-lg p-4 text-white placeholder-gray-600 focus:outline-none focus:border-neon-purple focus:ring-1 focus:ring-neon-purple transition-all"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-neon-purple hover:bg-purple-600 text-white font-bold py-4 rounded-lg transition-all transform active:scale-95 flex items-center justify-center space-x-2 mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <span>Atualizando...</span> : <span>DEFINIR NOVA SENHA</span>}
              </button>
            </form>
          ) : (
            <>
              <form onSubmit={handleAuth} className="space-y-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-2">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="player@dopamind.app"
                    className="w-full bg-background border border-gray-800 rounded-lg p-4 text-white placeholder-gray-600 focus:outline-none focus:border-neon-purple focus:ring-1 focus:ring-neon-purple transition-all"
                    required
                  />
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <label className="block text-xs uppercase tracking-wider font-bold text-gray-500">Senha</label>
                    {isLogin && (
                      <button
                        type="button"
                        onClick={() => {
                          setAuthView('forgot-password');
                          setError('');
                          setSuccessMessage('');
                        }}
                        className="text-xs font-semibold text-gray-400 transition-colors hover:text-white"
                      >
                        Esqueci a senha
                      </button>
                    )}
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-background border border-gray-800 rounded-lg p-4 text-white placeholder-gray-600 focus:outline-none focus:border-neon-purple focus:ring-1 focus:ring-neon-purple transition-all"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-neon-purple hover:bg-purple-600 text-white font-bold py-4 rounded-lg transition-all transform active:scale-95 flex items-center justify-center space-x-2 mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span>Carregando...</span>
                  ) : (
                    <>
                      <span>{isLogin ? 'ENTRAR NO GAME' : 'CRIAR PERSONAGEM'}</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 pt-6 border-t border-gray-800 text-center">
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-gray-400 hover:text-white text-sm transition-colors"
                >
                  {isLogin ? 'Ainda não tem conta? Criar agora.' : 'Já tem conta? Fazer login.'}
                </button>
              </div>
            </>
          )}

          {authView !== 'auth' && (
            <div className="mt-6 pt-6 border-t border-gray-800 text-center">
              <button
                type="button"
                onClick={() => {
                  setAuthView('auth');
                  setError('');
                  setSuccessMessage('');
                  setConfirmPassword('');
                }}
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                Voltar para login
              </button>
            </div>
          )}
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-gray-600 uppercase tracking-widest">Aura System v1.0</p>
          <p className="mt-3 text-[11px] leading-5 text-gray-500">
            Criado por
            <br />
            R² Solutions Group – Tech & Consulting
            <br />
            Mais do que Tech. Soluções com propósito
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
